import { createAuthenticatedAxiosInstance, createOrder } from "api/auth";
import { ICreateOrderRequest, ICreateOrderResponse } from "interfaces/IOrder";
import { IPaymentMethod } from "interfaces/IPaymentMethod";
import { CartItem } from "interfaces/ICart";
import { Address } from "interfaces/IAddresses";
import { IPayPalOrderData } from "interfaces/IPayPalOrder";

/**
 * Prepares the order data for PayPal
 * @param items - Array of cart items
 * @param selectedAddress - Selected delivery address
 * @returns IPayPalOrderData object
 */
export const preparePayPalOrderData = (
  items: CartItem[],
  selectedAddress: Address | null
): IPayPalOrderData => {
  return {
    id: new Date().getTime(),
    items: items.map((item) => ({
      id: item.sizeProductId,
      name: item.name,
      quantity: item.quantity,
      price: item.price,
    })),
    shippingAddress: selectedAddress || undefined,
  };
};

/**
 * Prepares the order data for creation
 * @param items - Array of cart items
 * @param selectedAddress - Selected delivery address
 * @param paymentMethod - Selected payment method
 * @param shippingCost - Shipping cost for the order
 * @returns ICreateOrderRequest object
 */
export const prepareOrderData = (
  items: CartItem[],
  selectedAddress: Address | null,
  paymentMethod: IPaymentMethod,
  shippingCost: number
): ICreateOrderRequest => {
  if (!selectedAddress) {
    throw new Error("No delivery address selected");
  }
  return {
    products: items.map((item) => ({
      price: item.price,
      quantity: item.quantity,
      size_product_id: item.sizeProductId,
    })),
    payment_method: parseInt(paymentMethod.id),
    delivery_address_id: selectedAddress.id,
    shipping_cost: shippingCost,
  };
};

/**
 * Processes an order with the selected payment method
 * @param orderData - The order data to create
 * @param paymentMethod - The selected payment method
 * @param paymentCompleted - Whether payment is completed
 * @param token - Auth token
 * @returns Promise with the order response and payment URL if applicable
 */
export const processOrder = async (
  orderData: ICreateOrderRequest,
  paymentMethod: IPaymentMethod,
  paymentCompleted: boolean = false,
  token: string | null
): Promise<{
  orderResponse: ICreateOrderResponse;
  paymentUrl?: string;
}> => {
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  // Create the order
  const orderResponse = await createOrder(axiosInstance, orderData);

  // Si el pago ya está completado (caso PayPal), solo devolver la respuesta
  if (paymentCompleted) {
    return { orderResponse };
  }

  // Para otros métodos de pago, devuelve la URL en lugar de redirigir directamente
  switch (paymentMethod.type.toLowerCase()) {
    case "paypal":
      return {
        orderResponse,
        paymentUrl: `https://www.paypal.com/checkoutnow?token=${orderResponse.order.id}`,
      };

    case "oxxo":
      return {
        orderResponse,
        paymentUrl: `https://oxxo.com/payment?order_id=${orderResponse.order.id}`,
      };

    case "mercadopago":
      return {
        orderResponse,
        paymentUrl: `https://mercadopago.com/checkout?order_id=${orderResponse.order.id}`,
      };

    case "bank_transfer":
      return {
        orderResponse,
        paymentUrl: `/bank-transfer/${orderResponse.order.id}`,
      };

    default:
      throw new Error("Unsupported payment method");
  }
};
